import { Injectable, computed, effect, inject } from '@angular/core';
import { isMembershipOnSale } from '@huddle-sports-platform/ui';
import {
  currentSeason,
  openSeason,
  sortSeasons,
} from '@huddle-sports-platform/schema/client';
import {
  AuthStore,
  combineSyncStatus,
  SyncedCollection,
} from '@huddle-sports-platform/app-core';
import {
  clubEventSchema,
  lotteryDrawSchema,
  lotteryEntrySchema,
  lotterySchema,
  memberSchema,
  eventTicketSchema,
  fundraiserSchema,
  installmentSchema,
  paymentRequestSchema,
  membershipPlanSchema,
  squadSchema,
  registrationSchema,
  transactionSchema,
  type ClubEvent,
  type Member,
  type ClubUser,
  clubUserSchema,
  type EventTicket,
  type Fundraiser,
  type Installment,
  type PaymentRequest,
  type MembershipPlan,
  type Squad,
  type Registration,
  type Transaction,
  sponsorSchema,
  type Sponsor,
  seasonSchema,
  type Season,
  type Lottery,
  type LotteryDraw,
  type LotteryEntry,
} from '@huddle-sports-platform/schema';
import { collection, limit, orderBy, query } from 'firebase/firestore';

import { db } from '../core/firebase';
import { ClubAdmin } from './club-admin';
import { ClubBrandStore } from './club-brand';

/** The transactions feed is limit()-bounded (an unbounded ledger). */
export const TRANSACTIONS_LIMIT = 200;

/** Two years of a weekly draw - enough for any history a club reads. */
export const DRAWS_LIMIT = 120;
/** Entries are the one lottery collection that grows without bound: the
 *  admin surfaces read the draw's own tallies, so this feed only has to
 *  cover the recent ones a records table shows. */
export const LOTTERY_ENTRIES_LIMIT = 300;

/** A squad with the id its doc key carries. */
export interface SquadEntry {
  readonly squadId: string;
  readonly squad: Squad;
}

/** A member the club has not finished describing, and what is missing. */
export interface MemberGap {
  readonly memberId: string;
  readonly name: string;
  /** In the words a banner can print: 'a date of birth', 'a sex'. */
  readonly missing: readonly string[];
}

/** A membership plan with the id its doc key carries. */
export interface PlanEntry {
  readonly planId: string;
  readonly plan: MembershipPlan;
}

/** A registration with its `{planId}_{memberId}` key unpacked. */
export interface RegistrationEntry {
  /** From the doc key `{seasonId}_{memberId}`. */
  readonly seasonId: string;
  readonly memberId: string;
  /** The plan is a FIELD on the doc now - a person changes plan without
   *  changing registration. */
  readonly planId: string;
  readonly registration: Registration;
}

/** A transaction with its ledger doc id, in feed (date desc) order. */
export interface TransactionEntry {
  readonly transactionId: string;
  readonly transaction: Transaction;
}

/**
 * THE CLUB'S OWN subtree, live, for the ADMIN AREA - hq's `clubSubtree`
 * (hq-store.ts) in single-club flavour. Eleven listeners on
 * `clubs/{clubId}/...`: users, membershipPlans, registrations,
 * transactions, installments, paymentRequests, fundraisers, events,
 * tickets and squads - everything the admin Finance and Members panes and
 * the Database tables read. The rules admit a club admin to all of them (kit
 * docs/data/security.md; the money ones via the finance:view cap).
 *
 * IN DataInit, claim-gated: {@link whenLoaded} warms the subtree up front
 * for an ADMIN and does nothing at all for a member, so the admin area
 * opens on live data instead of skeletons and members still never pay for
 * the footprint. The AdminShell's {@link start} call stays as the safety
 * net for the session that becomes an admin after init (a just-accepted
 * invitation), and the panes keep their `hud-async-state` skeletons for it.
 *
 * Transactions stay a warmed, limit()-bounded feed in v1; when a club's
 * ledger outgrows it, this table graduates to hq's cursor-browse pattern
 * (hq `data/accounts-browse.ts`: pageStarts, `limit(PAGE_SIZE + 1)`,
 * requestSeq, `getCountFromServer`) - the transactions table's toolbar
 * already lives outside `hud-async-state` for exactly that day.
 */
/** How long the club footprint may hold the boot veil before the admin app
 *  comes up regardless (see settled()). */
const WARM_TIMEOUT_MS = 8_000;

@Injectable({ providedIn: 'root' })
export class ClubDataStore {
  private readonly auth = inject(AuthStore);
  private readonly clubBrand = inject(ClubBrandStore);
  private readonly clubAdmin = inject(ClubAdmin);

  private readonly usersSync = new SyncedCollection<Member>(memberSchema, 'club-data:members');
  /** The club's LOGINS. Authority lives here since the people split, so a
   *  screen asking "who runs this club" reads this, not the roster. */
  private readonly loginsSync = new SyncedCollection<ClubUser>(
    clubUserSchema,
    'club-data:users',
  );
  private readonly seasonsSync = new SyncedCollection<Season>(
    seasonSchema,
    'club-data:seasons',
  );
  private readonly plansSync = new SyncedCollection<MembershipPlan>(
    membershipPlanSchema,
    'club-data:membershipPlans',
  );
  private readonly subscriptionsSync = new SyncedCollection<Registration>(
    registrationSchema,
    'club-data:registrations',
  );
  private readonly transactionsSync = new SyncedCollection<Transaction>(
    transactionSchema,
    'club-data:transactions',
  );
  private readonly installmentsSync = new SyncedCollection<Installment>(
    installmentSchema,
    'club-data:installments',
  );
  private readonly requestsSync = new SyncedCollection<PaymentRequest>(
    paymentRequestSchema,
    'club-data:paymentRequests',
  );
  private readonly fundraisersSync = new SyncedCollection<Fundraiser>(
    fundraiserSchema,
    'club-data:fundraisers',
  );
  private readonly eventsSync = new SyncedCollection<ClubEvent>(
    clubEventSchema,
    'club-data:events',
  );
  private readonly ticketsSync = new SyncedCollection<EventTicket>(
    eventTicketSchema,
    'club-data:tickets',
  );
  private readonly squadsSync = new SyncedCollection<Squad>(squadSchema, 'club-data:squads');
  private readonly sponsorsSync = new SyncedCollection<Sponsor>(
    sponsorSchema,
    'club-data:sponsors',
  );
  private readonly lotteriesSync = new SyncedCollection<Lottery>(
    lotterySchema,
    'club-data:lotteries',
  );
  private readonly drawsSync = new SyncedCollection<LotteryDraw>(
    lotteryDrawSchema,
    'club-data:lotteryDraws',
  );
  private readonly lotteryEntriesSync = new SyncedCollection<LotteryEntry>(
    lotteryEntrySchema,
    'club-data:lotteryEntries',
  );

  private readonly subtree = [
    this.usersSync,
    this.seasonsSync,
    this.plansSync,
    this.subscriptionsSync,
    this.transactionsSync,
    this.installmentsSync,
    this.requestsSync,
    this.fundraisersSync,
    this.eventsSync,
    this.ticketsSync,
    this.squadsSync,
    this.sponsorsSync,
    this.lotteriesSync,
    this.drawsSync,
    this.lotteryEntriesSync,
  ] as const;

  private readonly status = combineSyncStatus([...this.subtree]);

  /** First server snapshot still pending on any of the listeners. */
  readonly warming = this.status.warming;
  /** A listener died before its first server snapshot - the panes' retry. */
  readonly failed = this.status.failed;
  readonly live = this.status.live;

  /** The `uid|clubId` pair the current warm answers for (idempotence key -
   *  the same keying as ClubPaymentsStore). */
  private warmedFor: string | null = null;

  constructor() {
    // Sign-out drops the listeners; the next admin session re-warms.
    effect(() => {
      const uid = this.auth.user()?.uid ?? null;
      if (uid === null) {
        this.warmedFor = null;
        for (const sync of this.subtree) sync.detach();
      }
    });
  }

  /**
   * Attach the club subtree for the signed-in admin - imperative and
   * idempotent (the AdminShell calls it at every mount). A no-op without a
   * branded club (the dev shared app has no subtree to warm).
   */
  start(): void {
    const uid = this.auth.user()?.uid ?? null;
    const clubId = this.clubBrand.brand()?.clubId ?? null;
    if (!uid || !clubId) return;
    const key = `${uid}|${clubId}`;
    if (this.warmedFor === key) return;
    this.warmedFor = key;
    const sub = (name: string) => collection(db(), 'clubs', clubId, name);
    this.usersSync.warm(sub('members'));
    this.loginsSync.warm(sub('users'));
    // The club's seasons - a handful, and every money screen needs the
    // current one to say what year it is talking about.
    this.seasonsSync.warm(sub('seasons'));
    this.plansSync.warm(sub('membershipPlans'));
    this.subscriptionsSync.warm(sub('registrations'));
    this.transactionsSync.warm(
      query(sub('transactions'), orderBy('date', 'desc'), limit(TRANSACTIONS_LIMIT)),
    );
    // The payment schedule - the Membership pane's due-soon/overdue picture.
    // Unbounded like registrations: one doc per installment per member, the
    // same order of magnitude as the registrations themselves.
    this.installmentsSync.warm(sub('installments'));
    // The club's asks. Both are small by nature - a club runs a handful of
    // drives a year and a few dozen requests a year - so neither is
    // capped the way transactions are.
    this.requestsSync.warm(sub('paymentRequests'));
    this.fundraisersSync.warm(sub('fundraisers'));
    // Events and their tickets. An event list is naturally small; tickets
    // grow with attendance but stay the same order as registrations for a
    // community club - both graduate to cursor-browse if that changes.
    this.eventsSync.warm(sub('events'));
    this.ticketsSync.warm(sub('tickets'));
    // The squads a club runs - a handful, and the Members area's own
    // subject, so it warms with everything else rather than on demand.
    this.squadsSync.warm(sub('squads'));
    // Sponsor relationships - a handful per club.
    this.sponsorsSync.warm(sub('sponsors'));
    // The club lottery. A club runs one or two programmes and one draw a
    // week, so both are small; ENTRIES are not - a busy parish lotto mints
    // hundreds a week - so they are capped the way transactions are, and
    // the admin surfaces read the tallies on the draw rather than counting
    // the entries themselves.
    this.lotteriesSync.warm(sub('lotteries'));
    this.drawsSync.warm(query(sub('lotteryDraws'), orderBy('drawSeq', 'desc'), limit(DRAWS_LIMIT)));
    this.lotteryEntriesSync.warm(
      query(sub('lotteryEntries'), orderBy('createdAt', 'desc'), limit(LOTTERY_ENTRIES_LIMIT)),
    );
  }

  /**
   * DataInit's entry point: warm the subtree for THIS session and resolve
   * once every listener has its first server snapshot.
   *
   * Gated on the `adminOf` claim, which is the difference between warming
   * the club and warming nothing: a member opening twelve listeners they
   * may not read is twelve permanent `permission-denied` failures, a slow
   * and noisy way to learn what one claim already says. The claim is a
   * cache of the access doc, so this is a courtesy, not the boundary - the
   * rules still are.
   *
   * Settles on failure as well as success. A loader that never resolves
   * holds the boot veil forever (see DataInit's contract).
   */
  async whenLoaded(): Promise<void> {
    if (!this.auth.user()) return;
    // On the web the club comes from the address, resolved asynchronously;
    // a baked flavour is already settled by the time this runs.
    await this.whenBranded();
    await this.clubAdmin.whenSettled();
    if (!this.clubAdmin.isAdmin()) return;
    this.start();
    await this.settled();
  }

  /** The club lookup, when the address is still being resolved (web). */
  private whenBranded(): Promise<void> {
    if (this.clubBrand.settled()) return Promise.resolve();
    return new Promise((resolve) => {
      const timer = setInterval(() => {
        if (this.clubBrand.settled()) {
          clearInterval(timer);
          resolve();
        }
      }, 25);
    });
  }

  /**
   * Every listener's first server snapshot, or the first failure - CAPPED.
   * Resolves immediately when nothing attached (a brandless dev build).
   *
   * The cap matches the member store's: a listener that is attached and
   * healthy but never hears back from the server is neither warming
   * resolved nor failed, and an uncapped wait would hold the boot veil for
   * ever. The admin app comes up on what it has and keeps trying.
   */
  private settled(): Promise<void> {
    return new Promise((resolve) => {
      const deadline = Date.now() + WARM_TIMEOUT_MS;
      const timer = setInterval(() => {
        if (this.failed() || !this.warming() || Date.now() >= deadline) {
          clearInterval(timer);
          resolve();
        }
      }, 25);
    });
  }

  /** Re-attach after a failure (the panes' retry affordance). */
  retry(): void {
    this.warmedFor = null;
    for (const sync of this.subtree) sync.detach();
    this.start();
  }

  // ------------------------------------------------------------- raw maps

  readonly usersById = this.usersSync.docs;
  /** Keyed by the planId, which IS the doc key. */
  readonly plansByKey = this.plansSync.docs;
  /** Keyed by the `{planId}_{memberId}` composite doc key. */
  readonly subscriptionsByKey = this.subscriptionsSync.docs;

  // ------------------------------------------------------------ list views

  /** Every login at the club, keyed by uid. */
  readonly loginsByUid = this.loginsSync.docs;

  /**
   * The SIGNED-IN person's capabilities at this club, off their own login
   * doc - the same array the security rules read, so a surface that hides
   * a control on it is hiding something the backend would refuse anyway.
   *
   * UI only. Nothing here is a check: every Callable re-reads these caps
   * inside its own transaction, because a client copy can be stale by the
   * time a button is pressed.
   */
  readonly myCaps = computed<readonly string[]>(() => {
    const uid = this.auth.user()?.uid;
    return uid ? (this.loginsByUid().get(uid)?.caps ?? []) : [];
  });

  /**
   * Members the club has not finished describing - no date of birth, no
   * sex, or neither.
   *
   * A COMPUTED, not a call. Auto-assignment refuses to place somebody whose
   * dob or sex is unknown (see `criteriaMatches`), so this is the same
   * question asked from the other end: not "who could not be placed" after a
   * run, but "who can never be placed until somebody fills this in". Every
   * surface that cares - the banner over the squads table, the worklist on
   * the club overview - reads this one derivation off the same warmed data,
   * so no two of them can disagree about the count.
   */
  readonly membersNeedingInfo = computed<readonly MemberGap[]>(() =>
    this.members()
      .filter((m) => m.status !== 'archived')
      .map((m) => {
        const missing: string[] = [];
        if (!m.dob) missing.push('a date of birth');
        if (!m.sex) missing.push('a sex');
        return { memberId: m.memberId, name: `${m.firstName} ${m.lastName}`, missing };
      })
      .filter((gap) => gap.missing.length > 0),
  );

  /**
   * The same gaps, narrowed to PLAYERS - the only people a squad is for.
   *
   * A treasurer with no date of birth on file is a real gap in the club's
   * records (and `membersNeedingInfo` still says so), but no squad was ever
   * going to hold her, so counting her beside a squad table only inflates
   * the number and makes the matcher look worse than it is.
   */
  readonly playersNeedingInfo = computed<readonly MemberGap[]>(() => {
    const players = new Set(
      this.members()
        .filter((m) => m.memberTypes.includes('player'))
        .map((m) => m.memberId),
    );
    return this.membersNeedingInfo().filter((gap) => players.has(gap.memberId));
  });

  /** Every member of the club, sorted by name. */
  readonly members = computed<readonly Member[]>(() =>
    [...this.usersById().values()].sort((a, b) => a.nameLower.localeCompare(b.nameLower)),
  );

  readonly plans = computed<readonly PlanEntry[]>(() =>
    Array.from(this.plansByKey(), ([planId, plan]) => ({ planId, plan })),
  );

  readonly registrations = computed<readonly RegistrationEntry[]>(() =>
    Array.from(this.subscriptionsByKey(), ([key, registration]) => ({
      seasonId: key.slice(0, key.indexOf('_')),
      memberId: key.slice(key.indexOf('_') + 1),
      planId: registration.planId,
      registration,
    })),
  );

  /** The recent ledger, in the feed's (date desc) order. */
  readonly transactions = computed<readonly TransactionEntry[]>(() =>
    Array.from(this.transactionsSync.docs(), ([transactionId, transaction]) => ({
      transactionId,
      transaction,
    })),
  );

  /** Every installment on the club's schedule - app-core's
   *  summariseInstallments turns these into the due-soon/overdue picture. */
  readonly installments = computed<readonly Installment[]>(() => [
    ...this.installmentsSync.docs().values(),
  ]);

  /** Every ad-hoc ask the club has made, newest first. */
  readonly paymentRequests = computed(() =>
    Array.from(this.requestsSync.docs(), ([paymentRequestId, request]) => ({
      paymentRequestId,
      request,
    })).sort((a, b) => b.request.createdAt - a.request.createdAt),
  );

  /** The club's fundraising drives, newest first. */
  readonly fundraisers = computed(() =>
    Array.from(this.fundraisersSync.docs(), ([fundraiserId, fundraiser]) => ({
      fundraiserId,
      fundraiser,
    })).sort((a, b) => b.fundraiser.createdAt - a.fundraiser.createdAt),
  );

  /** The club's events, soonest first. */
  readonly events = computed(() =>
    Array.from(this.eventsSync.docs(), ([eventId, event]) => ({ eventId, event })).sort(
      (a, b) => a.event.startsAt - b.event.startsAt,
    ),
  );

  readonly eventsById = this.eventsSync.docs;

  /** Every admission the club has issued, newest first. */
  readonly tickets = computed(() =>
    Array.from(this.ticketsSync.docs(), ([ticketId, ticket]) => ({ ticketId, ticket })).sort(
      (a, b) => b.ticket.issuedAt - a.ticket.issuedAt,
    ),
  );

  readonly squadsById = this.squadsSync.docs;

  /** The club's sponsor relationships, alphabetical. */
  readonly sponsors = computed(() =>
    Array.from(this.sponsorsSync.docs(), ([sponsorId, sponsor]) => ({ sponsorId, sponsor })).sort(
      (a, b) => a.sponsor.nameLower.localeCompare(b.sponsor.nameLower),
    ),
  );

  /** The club's lottery programmes, newest first. A club runs one or two. */
  readonly lotteries = computed(() =>
    Array.from(this.lotteriesSync.docs(), ([lotteryId, lottery]) => ({ lotteryId, lottery })).sort(
      (a, b) => b.lottery.createdAt - a.lottery.createdAt,
    ),
  );
  readonly lotteriesById = this.lotteriesSync.docs;

  /** Every warmed draw, newest first - the Lottery pane's history. */
  readonly lotteryDraws = computed(() =>
    Array.from(this.drawsSync.docs(), ([drawId, draw]) => ({ drawId, draw })).sort(
      (a, b) => b.draw.drawSeq - a.draw.drawSeq,
    ),
  );

  /** The recent entries, newest first. Capped - see LOTTERY_ENTRIES_LIMIT. */
  readonly lotteryEntries = computed(() =>
    Array.from(this.lotteryEntriesSync.docs(), ([entryId, entry]) => ({ entryId, entry })).sort(
      (a, b) => b.entry.createdAt - a.entry.createdAt,
    ),
  );

  /**
   * The memberships a club is SELLING right now - what "the open season"
   * used to mean, derived from the plans themselves. A plan is live when
   * it is active, has not finished, and today sits inside whatever sale
   * window it carries.
   *
   * The clock is read once here rather than per surface, so every screen
   * agrees on what is on sale.
   */
  readonly livePlans = computed<readonly PlanEntry[]>(() => {
    const now = Date.now();
    const open = this.openSeason();
    return this.plans().filter(({ plan }) => isMembershipOnSale(plan, open, now));
  });

  /** The club's seasons, newest first. */
  readonly seasons = computed<readonly Season[]>(() =>
    sortSeasons([...this.seasonsSync.docs().values()]),
  );
  /** The season RUNNING - the one whose window contains today. */
  readonly currentSeason = computed(() =>
    currentSeason(this.seasons(), new Date().toISOString().slice(0, 10)),
  );
  /** The season taking registrations - at most one, server-enforced. */
  readonly openSeason = computed(() => openSeason(this.seasons()));
  /** What every screen calls the year it is showing: the open season if
   *  there is one, else the one running, else the newest. */
  readonly activeSeason = computed(
    () => this.openSeason() ?? this.currentSeason() ?? this.seasons()[0],
  );

  /** The ids of the memberships on sale - the cheap membership test the
   *  row-building surfaces do per registration. */
  readonly livePlanIds = computed(
    () => new Set(this.livePlans().map(({ planId }) => planId)),
  );

  /**
   * The registrations that count as CURRENT - the successor to "this
   * season's enrolments". A registration is current when the membership
   * it bought is still on sale; everything else is history.
   */
  readonly liveSubscriptions = computed<readonly RegistrationEntry[]>(() => {
    const live = this.livePlanIds();
    return this.registrations().filter(({ planId }) => live.has(planId));
  });

  /** The club's squads, by name - the Members area's own list. Archived
   *  ones stay in: a squad is never deleted, and an old panel is still
   *  the answer to "who was in it". */
  readonly squads = computed<readonly SquadEntry[]>(() =>
    Array.from(this.squadsById(), ([squadId, squad]) => ({ squadId, squad })).sort((a, b) =>
      a.squad.name.localeCompare(b.squad.name),
    ),
  );

  /** How many ACTIVE members each squad holds - the squad cards' subtitle.
   *  Counted once here rather than per card: `users.squadIds` is the M:N
   *  side, so a squad has no count of its own to read. */
  readonly squadSizes = computed<ReadonlyMap<string, number>>(() => {
    const sizes = new Map<string, number>();
    for (const user of this.usersById().values()) {
      if (user.status !== 'active') continue;
      for (const squadId of user.squadIds) sizes.set(squadId, (sizes.get(squadId) ?? 0) + 1);
    }
    return sizes;
  });

  /** Whether the club has any membership at all yet - the setup
   *  checklist's "no memberships" row reads it. */
  readonly hasMembership = computed(() => this.plans().length > 0);
}
